import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Fade,
  useTheme
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Info as InfoIcon,
  PlayArrow as PlayIcon,
  Lock as LockedIcon
} from '@mui/icons-material';
import * as d3 from 'd3';
import { MindMapNode, LearningStyleProfile, LearningResource } from '../../types/learning';

interface MindMapViewerProps {
  nodes: MindMapNode[];
  resources: LearningResource[];
  learningStyle: LearningStyleProfile;
  userProgress: Record<string, number>;
  onNodeClick: (node: MindMapNode) => void;
  onResourceClick: (resource: LearningResource) => void;
}

interface NodeData extends MindMapNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number | null;
  fy?: number | null;
}

const MindMapViewer: React.FC<MindMapViewerProps> = ({
  nodes,
  resources,
  learningStyle,
  userProgress,
  onNodeClick,
  onResourceClick
}) => {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<NodeData, any> | null>(null);
  const [selectedNode, setSelectedNode] = useState<MindMapNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<MindMapNode | null>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const zoomTransformRef = useRef<d3.ZoomTransform | null>(null);

  // Color scheme based on node types and mastery levels
  const getNodeColor = useCallback((node: MindMapNode) => {
    const masteryLevel = userProgress[node.id] || 0;
    const baseColors = {
      topic: theme.palette.primary.main,
      skill: theme.palette.secondary.main,
      resource: theme.palette.success.main,
      assessment: theme.palette.warning.main,
      prerequisite: theme.palette.grey[500]
    };

    const baseColor = baseColors[node.type] || theme.palette.grey[400];
    
    // Adjust opacity based on mastery level
    const opacity = 0.3 + (masteryLevel * 0.7);
    return `${baseColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  }, [userProgress, theme.palette]);

  const getNodeSize = useCallback((node: MindMapNode) => {
    const baseSize = 20;
    const difficultyMultiplier = 1 + (node.difficultyLevel / 10);
    const masteryMultiplier = 1 + (userProgress[node.id] || 0) * 0.5;
    return baseSize * difficultyMultiplier * masteryMultiplier;
  }, [userProgress]);

  const isNodeLocked = useCallback((node: MindMapNode) => {
    return node.prerequisites.some(prereqId => (userProgress[prereqId] || 0) < 0.5);
  }, [userProgress]);

  const isNodeCompleted = useCallback((node: MindMapNode) => {
    return (userProgress[node.id] || 0) >= 0.8;
  }, [userProgress]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current?.parentElement) {
        const containerRect = svgRef.current.parentElement.getBoundingClientRect();
        setDimensions({ width: containerRect.width, height: containerRect.height });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // D3 simulation setup
  useEffect(() => {
    if (!svgRef.current) return;

    const { width, height } = dimensions;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    
    // Set SVG dimensions and viewBox
    svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // Create a container group for all elements
    const container = svg.append("g").attr("class", "container");

    // Add animated gradient definitions for pulsing lines
    const defs = svg.append("defs");
    
    // Create filter for glow effect
    const filter = defs.append("filter")
      .attr("id", "bead-glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");
    
    filter.append("feGaussianBlur")
      .attr("stdDeviation", 2)
      .attr("result", "coloredBlur");
    
    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Stop existing simulation if it exists
    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    const simulation = d3.forceSimulation<NodeData>(nodes as NodeData[])
      .force("link", d3.forceLink<NodeData, any>()
        .id(d => d.id)
        .distance(150)
        .strength(0.1)
      )
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(d => getNodeSize(d as MindMapNode) + 10));

    simulationRef.current = simulation;

    // Add links based on prerequisites
    const links = nodes.flatMap(node => 
      node.prerequisites.map(prereqId => {
        const sourceNode = nodes.find(n => n.id === prereqId);
        const targetNode = node;
        if (sourceNode && targetNode) {
          return {
            source: sourceNode,
            target: targetNode
          };
        }
        return null;
      }).filter((link): link is { source: MindMapNode; target: MindMapNode } => link !== null)
    );

    // Create base lines
    const link = container.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(links)
      .enter().append("line")
      .attr("stroke", "#2196F3")
      .attr("stroke-width", 6)
      .attr("stroke-linecap", "round")
      .attr("opacity", 0.3)
      .style("cursor", "pointer")
      .style("filter", "drop-shadow(0 0 4px rgba(33, 150, 243, 0.5))")
      .on("mouseover", function() {
        d3.select(this)
          .attr("stroke-width", 8)
          .style("filter", "drop-shadow(0 0 12px rgba(33, 150, 243, 0.8))");
      })
      .on("mouseout", function() {
        d3.select(this)
          .attr("stroke-width", 6)
          .style("filter", "drop-shadow(0 0 4px rgba(33, 150, 243, 0.5))");
      });

    // Create flowing beads for each link
    const beadGroup = container.append("g")
      .attr("class", "beads");

    links.forEach((linkData, index) => {
      // Create 3 beads per link
      for (let i = 0; i < 3; i++) {
        const bead = beadGroup.append("circle")
          .attr("r", 3)
          .attr("fill", "#64B5F6")
          .attr("opacity", 0.9)
          .style("filter", "url(#bead-glow)");

        // Animate bead along the line
        const animateBead = () => {
          const source = linkData.source as any;
          const target = linkData.target as any;
          
          if (source && target && source.x !== undefined && source.y !== undefined && 
              target.x !== undefined && target.y !== undefined) {
            
            const startX = source.x;
            const startY = source.y;
            const endX = target.x;
            const endY = target.y;
            
            // Calculate distance and direction
            const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
            const angle = Math.atan2(endY - startY, endX - startX);
            
            // Start position with offset for multiple beads
            const offset = (i * distance * 0.3) % distance;
            const startOffsetX = startX + Math.cos(angle) * offset;
            const startOffsetY = startY + Math.sin(angle) * offset;
            
            bead
              .attr("cx", startOffsetX)
              .attr("cy", startOffsetY)
              .transition()
              .duration(2000 + (i * 500)) // Different speeds for each bead
              .ease(d3.easeLinear)
              .attr("cx", endX)
              .attr("cy", endY)
              .on("end", () => {
                // Restart animation
                setTimeout(animateBead, 100);
              });
          }
        };

        // Start animation after a delay for each bead
        setTimeout(animateBead, i * 700);
      }
    });

    const node = container.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(nodes as NodeData[])
      .enter().append("g")
      .attr("class", "node")
      .style("cursor", "grab")
      .call(d3.drag<SVGGElement, NodeData>()
        .on("start", (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
          d3.select(event.sourceEvent.target).style("cursor", "grabbing");
        })
        .on("drag", (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on("end", (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
          d3.select(event.sourceEvent.target).style("cursor", "grab");
        }))
      .on("click", (event, d) => {
        event.stopPropagation();
        if (!isNodeLocked(d)) {
          setSelectedNode(d);
          onNodeClick(d);
        }
      })
      .on("mouseover", (event, d) => {
        setHoveredNode(d);
      })
      .on("mouseout", () => {
        setHoveredNode(null);
      });

    // Add circles for nodes
    node.append("circle")
      .attr("r", d => getNodeSize(d))
      .attr("fill", d => getNodeColor(d))
      .attr("stroke", d => isNodeCompleted(d) ? theme.palette.success.main : theme.palette.grey[600])
      .attr("stroke-width", d => isNodeCompleted(d) ? 3 : 2)
      .attr("opacity", d => isNodeLocked(d) ? 0.5 : 1);

    // Add icons based on node type and status
    node.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("font-size", "16px")
      .text(d => {
        if (isNodeCompleted(d)) return "✓";
        if (isNodeLocked(d)) return "🔒";
        switch (d.type) {
          case 'topic': return "📚";
          case 'skill': return "🎯";
          case 'resource': return "📖";
          case 'assessment': return "❓";
          case 'prerequisite': return "🔗";
          default: return "●";
        }
      });

    // Add labels with better positioning and full text
    const labels = node.append("g")
      .attr("class", "label-group")
      .attr("transform", d => `translate(0, ${getNodeSize(d) + 25})`);

    // Function to wrap text
    const wrapText = (text: string, width: number) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine + ' ' + word;
        if (testLine.length * 6 > width) { // Approximate character width
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    labels.each(function(d) {
      const labelGroup = d3.select(this);
      const maxWidth = 120; // Maximum width for text wrapping
      const lines = wrapText(d.title, maxWidth);
      
      lines.forEach((line, i) => {
        labelGroup.append("text")
          .attr("text-anchor", "middle")
          .attr("dy", i * 14) // Line height
          .attr("font-size", "11px")
          .attr("fill", theme.palette.text.primary)
          .attr("font-weight", "500")
          .text(line);
      });
    });

    // Add progress rings for completed nodes
    node.filter(d => isNodeCompleted(d))
      .append("circle")
      .attr("r", d => getNodeSize(d) + 5)
      .attr("fill", "none")
      .attr("stroke", theme.palette.success.main)
      .attr("stroke-width", 2)
      .attr("opacity", 0.8);

    simulation.on("tick", () => {
      link
        .attr("x1", d => (d.source as any).x)
        .attr("y1", d => (d.source as any).y)
        .attr("x2", d => (d.target as any).x)
        .attr("y2", d => (d.target as any).y);

      node
        .attr("transform", d => `translate(${(d as any).x},${(d as any).y})`);

      // Update label positions
      labels
        .attr("transform", d => `translate(0, ${getNodeSize(d) + 25})`);
    });

    // Start the simulation
    simulation.restart();

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', event.transform.toString());
        zoomTransformRef.current = event.transform;
      });

    svg.call(zoom);

    // Apply stored transform if it exists
    if (zoomTransformRef.current) {
      svg.call(zoom.transform, zoomTransformRef.current);
    }

    // Click handler for background
    svg.on('click', () => {
      setSelectedNode(null);
    });

    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
        simulationRef.current = null;
      }
    };
  }, [nodes, userProgress, theme, dimensions, getNodeColor, getNodeSize, isNodeCompleted, isNodeLocked, onNodeClick]);

  const handleZoomIn = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      const currentTransform = zoomTransformRef.current || d3.zoomIdentity;
      const newTransform = currentTransform.scale(Math.min(currentTransform.k * 1.2, 4));
      svg.transition().duration(300).call(
        d3.zoom<SVGSVGElement, unknown>().transform,
        newTransform
      );
      zoomTransformRef.current = newTransform;
    }
  };

  const handleZoomOut = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      const currentTransform = zoomTransformRef.current || d3.zoomIdentity;
      const newTransform = currentTransform.scale(Math.max(currentTransform.k / 1.2, 0.1));
      svg.transition().duration(300).call(
        d3.zoom<SVGSVGElement, unknown>().transform,
        newTransform
      );
      zoomTransformRef.current = newTransform;
    }
  };

  const handleCenter = () => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      const resetTransform = d3.zoomIdentity;
      svg.transition().duration(300).call(
        d3.zoom<SVGSVGElement, unknown>().transform,
        resetTransform
      );
      zoomTransformRef.current = resetTransform;
    }
  };


  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: '100%',
      '@keyframes pulse': {
        '0%': {
          left: '-100%'
        },
        '100%': {
          left: '100%'
        }
      },
      '@keyframes flow': {
        '0%': {
          left: '0%'
        },
        '100%': {
          left: '100%'
        }
      }
    }}>
      {/* Controls */}
      <Box sx={{ 
        position: 'absolute', 
        top: 16, 
        right: 16, 
        zIndex: 10,
        display: 'flex',
        gap: 1
      }}>
        <Tooltip title="Zoom In">
          <IconButton onClick={handleZoomIn} size="small">
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton onClick={handleZoomOut} size="small">
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Center View">
          <IconButton onClick={handleCenter} size="small">
            <CenterIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Connection Legend */}
      <Paper sx={{ 
        position: 'absolute', 
        bottom: 16, 
        left: 16, 
        zIndex: 10,
        p: 2,
        maxWidth: 200
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Connection Types
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 40, 
              height: 6, 
              background: '#2196F3',
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 0 4px rgba(33, 150, 243, 0.5)',
              opacity: 0.3,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '0%',
                width: 6,
                height: 6,
                background: '#64B5F6',
                borderRadius: '50%',
                transform: 'translateY(-50%)',
                animation: 'flow 2s infinite linear',
                boxShadow: '0 0 8px rgba(100, 181, 246, 0.8)'
              }
            }} />
            <Typography variant="caption">Prerequisites (Flowing Beads)</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Mind Map SVG */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          position: 'relative'
        }}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ 
            background: theme.palette.background.paper,
            display: 'block'
          }}
        />
      </Box>

      {/* Node Details Panel */}
      <Fade in={!!selectedNode} timeout={300}>
        <Paper
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            p: 2,
            maxWidth: 300,
            zIndex: 1000,
            display: selectedNode ? 'block' : 'none',
            backgroundColor: 'background.paper',
            boxShadow: 3
          }}
        >
          {selectedNode && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {selectedNode.title}
                </Typography>
                <IconButton size="small" onClick={() => setSelectedNode(null)}>
                  <InfoIcon />
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedNode.description}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Progress: {Math.round((userProgress[selectedNode.id] || 0) * 100)}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(userProgress[selectedNode.id] || 0) * 100}
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip 
                  label={`Level ${selectedNode.difficultyLevel}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
                <Chip 
                  label={`${selectedNode.estimatedTimeMinutes}min`} 
                  size="small" 
                  color="secondary" 
                  variant="outlined"
                />
                <Chip 
                  label={selectedNode.type} 
                  size="small" 
                  color="default" 
                  variant="outlined"
                />
              </Box>

              {isNodeLocked(selectedNode) && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  color: 'warning.main',
                  mb: 2
                }}>
                  <LockedIcon fontSize="small" />
                  <Typography variant="caption">
                    Complete prerequisites first
                  </Typography>
                </Box>
              )}

              {!isNodeLocked(selectedNode) && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => onNodeClick(selectedNode)}
                  >
                    <PlayIcon />
                  </IconButton>
                  <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                    Start Learning
                  </Typography>
                </Box>
              )}

              {/* Learning Objectives */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Learning Objectives:
                </Typography>
                {selectedNode.learningObjectives.map((objective, index) => (
                  <Typography key={index} variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • {objective}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </Paper>
      </Fade>

      {/* Hover Tooltip */}
      {hoveredNode && (
        <Tooltip
          title={
            <Box>
              <Typography variant="subtitle2">{hoveredNode.title}</Typography>
              <Typography variant="caption">
                {hoveredNode.type} • Level {hoveredNode.difficultyLevel}
              </Typography>
            </Box>
          }
          placement="top"
          arrow
        >
          <Box />
        </Tooltip>
      )}
    </Box>
  );
};

export default MindMapViewer;
